import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import TodoList from '../components/TodoList';
import CreateListForm from '../components/CreateListForm';
import Sidebar from '../components/Sidebar';
import RightPanel from '../components/RightPanel';
import MobileHeader from '../components/MobileHeader';
import TaskDrawer from '../components/TaskDrawer';
import { ListType, isMainList, isTomorrowList, isDailyHabitsList } from '../constants/listTypes';
import { isAfterPromotionCutoff, shouldShowList } from '../utils/timeUtils';

async function detectCommonSubtaskPattern(tasks) {
  const tasksWithSubs = tasks.filter(t => !t.completed && t.subTasksCount > 0);
  if (tasksWithSubs.length < 3) return null;

  const sample = tasksWithSubs.slice(0, 5);
  const subtaskSets = await Promise.all(
    sample.map(t => api.getSubTasks(t.id))
  );

  const signatures = subtaskSets.map(subs =>
    subs.sort((a, b) => a.position - b.position).map(s => s.text).join('\n')
  );

  const counts = {};
  signatures.forEach(sig => { counts[sig] = (counts[sig] || 0) + 1; });
  const commonSig = Object.entries(counts).find(([, count]) => count >= 3);
  if (!commonSig) return null;

  return commonSig[0].split('\n');
}

export default function MainView({ lists, setLists, showCreateForm, setShowCreateForm, onDeleteList, onReloadLists, onLogout, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const tomorrowCutoffHour = user?.tomorrow_cutoff_hour ?? 18;
  const expandedSubtasksEnabled = user?.expanded_subtasks_enabled || false;
  const priorityModeEnabled = user?.priority_mode_enabled || false;
  const [listViewPreferences, setListViewPreferences] = useState({});

  useEffect(() => {
    api.getListViewPreferences().then(prefs => setListViewPreferences(prefs)).catch(() => {});
  }, []);

  const handleSaveListViewPreference = useCallback((listId, viewMode) => {
    setListViewPreferences(prev => ({ ...prev, [listId]: viewMode }));
    api.setListViewPreference(listId, viewMode).catch(() => {});
  }, []);

  // Derive activeListId from URL params - URL is the single source of truth
  const listIdFromUrl = params.listId;

  // Get default list if no URL param (when on root route)
  const getDefaultListId = useCallback(() => {
    const mainList = lists.find(l => l.list_type === ListType.MAIN);
    return mainList?.id || lists[0]?.id || null;
  }, [lists]);

  // Active list ID is either from URL or default list
  const activeListId = listIdFromUrl || getDefaultListId();

  // Memoize expensive computations to avoid recalculating on every render
  const activeList = useMemo(
    () => lists.find(list => list.id === activeListId),
    [lists, activeListId]
  );
  const isMainListValue = useMemo(
    () => activeList && isMainList(activeList.list_type),
    [activeList]
  );
  const isTomorrowListValue = useMemo(
    () => activeList && isTomorrowList(activeList.list_type),
    [activeList]
  );
  const visibleLists = useMemo(
    () => lists.filter(list => shouldShowList(list.list_type, tomorrowCutoffHour)),
    [lists, tomorrowCutoffHour]
  );
  const [drawerTask, setDrawerTask] = useState(null);
  const commonSubtaskPattern = useRef(null);

  // Detect common subtask pattern when active list changes
  useEffect(() => {
    if (activeList && activeList.tasks) {
      detectCommonSubtaskPattern(activeList.tasks)
        .then(pattern => { commonSubtaskPattern.current = pattern; })
        .catch(() => { commonSubtaskPattern.current = null; });
    } else {
      commonSubtaskPattern.current = null;
    }
  }, [activeListId, activeList]);

  // Reload lists when navigating back from account page
  useEffect(() => {
    if (location.pathname === '/') {
      onReloadLists();
    }
  }, [location.pathname, onReloadLists]);

  // Check if active list is still visible, switch if not
  useEffect(() => {
    if (activeListId && activeList) {
      if (!shouldShowList(activeList.list_type, tomorrowCutoffHour)) {
        // Active list is now hidden (tomorrow list before cutoff), switch to main list
        const mainList = lists.find(l => l.list_type === ListType.MAIN && shouldShowList(l.list_type, tomorrowCutoffHour));
        if (mainList) {
          navigate(`/list/${mainList.id}`, { replace: true });
        }
      }
    }
  }, [activeListId, activeList, lists, navigate, tomorrowCutoffHour]);

  // Redirect to default list URL if on root route
  useEffect(() => {
    if (location.pathname === '/' && activeListId && !listIdFromUrl) {
      navigate(`/list/${activeListId}`, { replace: true });
    }
  }, [location.pathname, activeListId, listIdFromUrl, navigate]);

  // Update drawer task when lists change (but only if drawer is open)
  useEffect(() => {
    if (drawerTask) {
      const allTasks = lists.flatMap(list => list.tasks);
      let updatedTask = allTasks.find(t => t.id === drawerTask.id);
      if (updatedTask) {
        if (updatedTask.text !== drawerTask.text || updatedTask.completed !== drawerTask.completed) {
          setDrawerTask(updatedTask);
        }
      } else if (drawerTask.id.startsWith('temp-')) {
        // Optimistic task was replaced with real task from API - find it by text match
        updatedTask = allTasks.find(t => t.text === drawerTask.text && !t.id.startsWith('temp-'));
        if (updatedTask) {
          setDrawerTask(updatedTask);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lists]);

  // Scroll to top when switching lists or returning to main view
  // Consolidated scroll effect to avoid duplication
  const mainContentRef = useRef(null);
  const prevActiveListIdRef = useRef(activeListId);

  useLayoutEffect(() => {
    const listChanged = activeListId && activeListId !== prevActiveListIdRef.current;
    const returnedToHome = location.pathname === '/' && activeListId;

    if (listChanged || returnedToHome) {
      if (listChanged) {
        prevActiveListIdRef.current = activeListId;
      }

      // Scroll instantly without animation
      const mainContent = mainContentRef.current || document.querySelector('.main-content');
      if (mainContent) {
        mainContent.scrollTop = 0;
        mainContent.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [activeListId, location.pathname]);

  return (
    <div className="App">
      <MobileHeader onLogout={onLogout} />
      <div className="app-layout">
        <Sidebar
          lists={visibleLists}
          activeListId={activeListId}
          onSelectList={(listId) => {
            navigate(`/list/${listId}`);
            setShowCreateForm(false);
          }}
          onCreateNew={() => setShowCreateForm(true)}
          onLogout={onLogout}
        />

        <main className="main-content" ref={mainContentRef}>
          <div className="content-body">
            {showCreateForm ? (
              <div className="create-form-container">
                <CreateListForm
                  onCreateList={async (name) => {
                    const newList = await api.createList(name);
                    // Check if this list already exists in our lists (e.g., Daily Habits)
                    const existingList = lists.find(l => l.id === newList.id);
                    if (!existingList) {
                      setLists([...lists, newList]);
                    } else {
                      // List already exists, just refresh to get latest data
                      const data = await api.getLists();
                      setLists(data);
                    }
                    setShowCreateForm(false);
                    navigate(`/list/${newList.id}`);
                  }}
                  onCancel={() => setShowCreateForm(false)}
                />
              </div>
            ) : activeList ? (
              <TodoList
                list={activeList}
                isMainList={isMainListValue}
                isTomorrowList={isTomorrowListValue}
                onRenameList={async (listId, name) => {
                  await api.updateList(listId, name);
                  const data = await api.getLists();
                  setLists(data);
                }}
                onAddTask={async (listId, text) => {
                  const activeList = lists.find(l => l.id === listId);
                  const isDailyHabits = activeList && isDailyHabitsList(activeList.list_type);
                  const isShared = activeList?.is_shared || false;

                  // Build createdBy object if list is shared and user info is available
                  const createdBy = (isShared && user?.id && user?.name)
                    ? { userId: user.id, name: user.name }
                    : null;

                  // For daily habits, optimistically add the task immediately
                  if (isDailyHabits) {
                    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                    const patternToApply = commonSubtaskPattern.current;
                    const optimisticTask = {
                      id: tempId,
                      text: text,
                      completed: false,
                      createdAt: new Date().toISOString(),
                      position: activeList.tasks.length,
                      isDailyHabit: true,
                      ...(createdBy && { createdBy }),
                      ...(patternToApply && { subTasksCount: patternToApply.length, completedSubTasksCount: 0 })
                    };

                    // Optimistically update UI
                    setLists(prevLists => prevLists.map(list =>
                      list.id === listId
                        ? { ...list, tasks: [...list.tasks, optimisticTask] }
                        : list
                    ));

                    // Fire API call in the background; on success, replace with real task; on failure, reload
                    api.addTask(listId, text, { subTasks: patternToApply || undefined })
                      .then(newTask => {
                        // Always preserve createdBy - use API response if available, otherwise keep optimistic one
                        const taskWithFlag = {
                          ...newTask,
                          isDailyHabit: true,
                          createdBy: newTask.createdBy || createdBy || undefined
                        };
                        // Remove undefined createdBy to keep response clean
                        if (!taskWithFlag.createdBy) {
                          delete taskWithFlag.createdBy;
                        }
                        setLists(prevLists => prevLists.map(list =>
                          list.id === listId
                            ? {
                                ...list,
                                tasks: list.tasks.map(t => t.id === tempId ? taskWithFlag : t)
                              }
                            : list
                        ));
                      })
                      .catch(async (error) => {
                        console.error('Error adding task:', error);
                        try {
                          const data = await api.getLists();
                          setLists(data);
                        } catch (reloadError) {
                          console.error('Error reloading lists after add failure:', reloadError);
                        }
                      });
                  } else {
                    // For non-daily habits, optimistically add the task
                    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                    const patternToApply = commonSubtaskPattern.current;
                    const optimisticTask = {
                      id: tempId,
                      text: text,
                      completed: false,
                      createdAt: new Date().toISOString(),
                      position: activeList.tasks.length,
                      ...(createdBy && { createdBy }),
                      ...(patternToApply && { subTasksCount: patternToApply.length, completedSubTasksCount: 0 })
                    };

                    // Optimistically update UI
                    setLists(prevLists => prevLists.map(list =>
                      list.id === listId
                        ? { ...list, tasks: [...list.tasks, optimisticTask] }
                        : list
                    ));

                    // Fire API call in the background
                    api.addTask(listId, text, { subTasks: patternToApply || undefined })
                      .then(newTask => {
                        // Always preserve createdBy - use API response if available, otherwise keep optimistic one
                        const taskWithCreator = {
                          ...newTask,
                          createdBy: newTask.createdBy || createdBy || undefined
                        };
                        // Remove undefined createdBy to keep response clean
                        if (!taskWithCreator.createdBy) {
                          delete taskWithCreator.createdBy;
                        }
                        setLists(prevLists => prevLists.map(list =>
                          list.id === listId
                            ? {
                                ...list,
                                tasks: list.tasks.map(t => t.id === tempId ? taskWithCreator : t)
                              }
                            : list
                        ));
                      })
                      .catch(async (error) => {
                        console.error('Error adding task:', error);
                        try {
                          const data = await api.getLists();
                          setLists(data);
                        } catch (reloadError) {
                          console.error('Error reloading lists after add failure:', reloadError);
                        }
                      });
                  }
                }}
                onToggleTask={async (listId, taskId) => {
                  await api.toggleTask(taskId);
                  const data = await api.getLists();
                  setLists(data);
                }}
                onDeleteTask={async (listId, taskId) => {
                  const activeList = lists.find(l => l.id === listId);
                  const isDailyHabits = activeList && isDailyHabitsList(activeList.list_type);

                  // For daily habits, optimistically remove the task immediately
                  if (isDailyHabits) {
                    // Optimistically update UI
                    setLists(prevLists => prevLists.map(list =>
                      list.id === listId
                        ? { ...list, tasks: list.tasks.filter(t => t.id !== taskId) }
                        : list
                    ));

                    // Fire API call in the background; on failure, reload lists
                    api.deleteTask(taskId)
                      .catch(async (error) => {
                        console.error('Error deleting task:', error);
                        try {
                          const data = await api.getLists();
                          setLists(data);
                        } catch (reloadError) {
                          console.error('Error reloading lists after delete failure:', reloadError);
                        }
                      });
                  } else {
                    // For non-daily habits, use the existing synchronous approach
                    await api.deleteTask(taskId);
                    const data = await api.getLists();
                    setLists(data);
                  }
                }}
                onSnoozeTask={async (mainListId, promotedTaskId, originalTaskId, sourceListId) => {
                  // Optimistically update UI: remove from main list and add to tomorrow list
                  setLists(prevLists => {
                    const tomorrowList = prevLists.find(l => l.list_type === ListType.TOMORROW);
                    const mainList = prevLists.find(l => l.id === mainListId);
                    const taskToMove = mainList?.tasks.find(t => t.id === promotedTaskId);

                    return prevLists.map(list => {
                      // Remove from main list
                      if (list.id === mainListId) {
                        return {
                          ...list,
                          tasks: list.tasks.filter(task => task.id !== promotedTaskId),
                        };
                      }

                      // Add to tomorrow list
                      if (tomorrowList && list.id === tomorrowList.id && taskToMove) {
                        return {
                          ...list,
                          tasks: [...list.tasks, {
                            ...taskToMove,
                            originalTaskId: originalTaskId,
                            sourceList: sourceListId ? prevLists.find(l => l.id === sourceListId) : null,
                          }],
                        };
                      }

                      // Update the original task in its source list (if known) to show it's promoted to tomorrow
                      if (sourceListId && list.id === sourceListId) {
                        return {
                          ...list,
                          tasks: list.tasks.map(task =>
                            task.id === originalTaskId
                              ? {
                                  ...task,
                                  promoted: true,
                                  promotedToList: 'tomorrow',
                                }
                              : task
                          ),
                        };
                      }

                      // Fallback: search for the original task by id in any list
                      const hasOriginal = list.tasks.some(task => task.id === originalTaskId);
                      if (hasOriginal) {
                        return {
                          ...list,
                          tasks: list.tasks.map(task =>
                            task.id === originalTaskId
                              ? {
                                  ...task,
                                  promoted: true,
                                  promotedToList: 'tomorrow',
                                }
                              : task
                          ),
                        };
                      }

                      return list;
                    });
                  });

                  // Fire API call in the background; on failure, log and reload lists
                  api.snoozeTask(promotedTaskId).catch(async (error) => {
                    console.error('Error snoozing task:', error);
                    try {
                      const data = await api.getLists();
                      setLists(data);
                    } catch (reloadError) {
                      console.error('Error reloading lists after snooze failure:', reloadError);
                    }
                  });
                }}
                onPromoteTask={async (sourceListId, taskId) => {
                  const useTomorrow = isAfterPromotionCutoff(tomorrowCutoffHour);
                  const promotedToList = useTomorrow ? 'tomorrow' : 'main';

                  // Optimistically update the original task with promotion info
                  setLists(prevLists => prevLists.map(list => {
                    if (list.id === sourceListId) {
                      return {
                        ...list,
                        tasks: list.tasks.map(task =>
                          task.id === taskId
                            ? {
                                ...task,
                                promoted: true,
                                promotedToList: promotedToList
                              }
                            : task
                        )
                      };
                    }
                    return list;
                  }));

                  const promoteResult = await api.promoteToMain(taskId, useTomorrow);
                  const { originalTask, promotedTask } = promoteResult;
                  // The backend resolves priority slots server-side and returns them
                  // (lightweight: task referenced by id). Present only when promoting
                  // to main under priority mode. We hydrate each id into the full task
                  // object we already hold -- no client-side pick logic.
                  const resolvedSlots = promoteResult.priority_slots;
                  const targetListType = useTomorrow ? ListType.TOMORROW : ListType.MAIN;
                  // Carry the source list on the promoted task so it reconciles cleanly
                  // with the getLists shape (which includes sourceList on main tasks).
                  const sourceList = lists.find(l => l.id === sourceListId);
                  const promotedTaskWithSource = sourceList
                    ? { ...promotedTask, sourceList: { id: sourceList.id, name: sourceList.name } }
                    : promotedTask;
                  setLists(prevLists => prevLists.map(list => {
                    if (list.list_type === targetListType) {
                      const updated = { ...list, tasks: [...list.tasks, promotedTaskWithSource] };
                      if (targetListType === ListType.MAIN && Array.isArray(resolvedSlots)) {
                        const taskById = new Map(updated.tasks.map(t => [t.id, t]));
                        updated.priority_slots = resolvedSlots.map(slot => ({
                          slot_id: slot.slot_id,
                          list_id: slot.list_id,
                          list_name: slot.list_name,
                          position: slot.position,
                          fulfilled: slot.fulfilled,
                          task: slot.task_id ? (taskById.get(slot.task_id) || null) : null,
                        }));
                      }
                      return updated;
                    } else if (list.id === sourceListId) {
                      return {
                        ...list,
                        tasks: list.tasks.map(task =>
                          task.id === taskId ? originalTask : task
                        )
                      };
                    }
                    return list;
                  }));
                }}
                onDemoteTask={async (mainListId, promotedTaskId, originalTaskId, sourceListId) => {
                  // Optimistically update UI: remove from main list and clear promotion on original
                  setLists(prevLists =>
                    prevLists.map(list => {
                      // Remove from main list immediately
                      if (list.id === mainListId) {
                        return {
                          ...list,
                          tasks: list.tasks.filter(task => task.id !== promotedTaskId),
                        };
                      }

                      // Update the original task in its source list (if known)
                      if (sourceListId && list.id === sourceListId) {
                        return {
                          ...list,
                          tasks: list.tasks.map(task =>
                            task.id === originalTaskId
                              ? {
                                  ...task,
                                  promoted: false,
                                  promotedAt: null,
                                  promotedTaskId: null,
                                }
                              : task
                          ),
                        };
                      }

                      // Fallback: search for the original task by id in any list
                      const hasOriginal = list.tasks.some(task => task.id === originalTaskId);
                      if (hasOriginal) {
                        return {
                          ...list,
                          tasks: list.tasks.map(task =>
                            task.id === originalTaskId
                              ? {
                                  ...task,
                                  promoted: false,
                                  promotedAt: null,
                                  promotedTaskId: null,
                                }
                              : task
                          ),
                        };
                      }

                      return list;
                    })
                  );

                  // Fire API call in the background; on failure, log and reload lists
                  api.demoteFromMain(promotedTaskId).catch(async (error) => {
                    console.error('Error demoting task:', error);
                    try {
                      const data = await api.getLists();
                      setLists(data);
                    } catch (reloadError) {
                      console.error('Error reloading lists after demote failure:', reloadError);
                    }
                  });
                }}
                onDeleteList={onDeleteList}
                onClearCompleted={async (listId, taskIds) => {
                  await api.deleteMultipleTasks(taskIds);
                  const data = await api.getLists();
                  setLists(data);
                }}
                onUpdateTask={async (taskId, text) => {
                  // Find the task to check if it's in a daily habits list
                  const taskList = lists.find(list => list.tasks.some(t => t.id === taskId));
                  const isDailyHabits = taskList && isDailyHabitsList(taskList.list_type);

                  // For daily habits, optimistically update the task text immediately
                  if (isDailyHabits) {
                    // Optimistically update UI
                    setLists(prevLists => prevLists.map(list => ({
                      ...list,
                      tasks: list.tasks.map(task =>
                        task.id === taskId
                          ? { ...task, text: text }
                          : task
                      )
                    })));

                    // Fire API call in the background; on failure, reload lists
                    api.updateTask(taskId, text)
                      .then(() => {
                        // Optionally refresh to get any server-side updates
                        // For now, we'll just keep the optimistic update
                      })
                      .catch(async (error) => {
                        console.error('Error updating task:', error);
                        try {
                          const data = await api.getLists();
                          setLists(data);
                        } catch (reloadError) {
                          console.error('Error reloading lists after update failure:', reloadError);
                        }
                      });
                  } else {
                    // For non-daily habits, use the existing synchronous approach
                    await api.updateTask(taskId, text);
                    const data = await api.getLists();
                    setLists(data);
                  }
                }}
                onReorderTasks={(listId, orderedTaskIds) => {
                  // Optimistically reorder tasks in local state
                  setLists(prevLists =>
                    prevLists.map(list => {
                      if (list.id !== listId) return list;
                      const idToTask = {};
                      list.tasks.forEach(task => {
                        idToTask[task.id] = task;
                      });
                      const reorderedTasks = orderedTaskIds
                        .map(id => idToTask[id])
                        .filter(Boolean);
                      return { ...list, tasks: reorderedTasks };
                    })
                  );

                  // Fire API call in the background; on failure, log and reload lists
                  api.reorderTasks(listId, orderedTaskIds).catch(async (error) => {
                    console.error('Error reordering tasks:', error);
                    try {
                      const data = await api.getLists();
                      setLists(data);
                    } catch (reloadError) {
                      console.error('Error reloading lists after reorder failure:', reloadError);
                    }
                  });
                }}
                lists={visibleLists}
                tomorrowCutoffHour={tomorrowCutoffHour}
                onSelectList={(listId) => {
                  navigate(`/list/${listId}`);
                  setShowCreateForm(false);
                }}
                onCreateNew={() => setShowCreateForm(true)}
                onOpenTaskDrawer={(task) => setDrawerTask(task)}
                onReloadLists={async () => {
                  const data = await api.getLists();
                  setLists(data);
                }}
                listViewPreferences={listViewPreferences}
                onSaveListViewPreference={handleSaveListViewPreference}
                expandedSubtasksEnabled={expandedSubtasksEnabled}
                priorityModeEnabled={priorityModeEnabled}
              />
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📝</div>
                <p className="empty-state-text">List not found</p>
              </div>
            )}
          </div>
        </main>

        <RightPanel activeList={activeList} />
      </div>

      {/* Task Drawer */}
      {drawerTask && activeList && (
        <TaskDrawer
          task={drawerTask}
          listId={activeList.id}
          lists={lists}
          tomorrowCutoffHour={tomorrowCutoffHour}
          setLists={setLists}
          isOpen={!!drawerTask}
          onClose={() => setDrawerTask(null)}
          onUpdateTask={async (taskId, text) => {
            await api.updateTask(taskId, text);
            const data = await api.getLists();
            setLists(data);
            // Update drawer task if it's the same task
            if (drawerTask && drawerTask.id === taskId) {
              const updatedTask = data
                .flatMap(list => list.tasks)
                .find(t => t.id === taskId);
              if (updatedTask) {
                setDrawerTask(updatedTask);
              }
            }
          }}
          onToggleTask={async (listId, taskId) => {
            await api.toggleTask(taskId);
            const data = await api.getLists();
            setLists(data);
            // Update drawer task if it's the same task
            if (drawerTask && drawerTask.id === taskId) {
              const updatedTask = data
                .flatMap(list => list.tasks)
                .find(t => t.id === taskId);
              if (updatedTask) {
                setDrawerTask(updatedTask);
              }
            }
          }}
          onDeleteTask={async (listId, taskId) => {
            await api.deleteTask(taskId);
            const data = await api.getLists();
            setLists(data);
            setDrawerTask(null);
          }}
          onTaskDataChange={async (taskId) => {
            // Refresh lists to update note icon and progress bar
            const data = await api.getLists();
            setLists(data);
            // Update drawer task if it's the same task
            if (drawerTask && drawerTask.id === taskId) {
              const updatedTask = data
                .flatMap(list => list.tasks)
                .find(t => t.id === taskId);
              if (updatedTask) {
                setDrawerTask(updatedTask);
              }
            }
          }}
        />
      )}
    </div>
  );
}
